import Axios from "axios";
import { useRemoteQuery } from "../useRemote/useRemoteQuery";
import { BookableBookingObjectDto } from "../../../features/Schedule/typings/resource-calendar.types";
import { BookingType } from "../../../features/Booking-Form/typings/booking-inputs";

export type FetchBookableBookingObjectsParams = {
  startDate: string;
  endDate: string;
  bookingType: BookingType;
  includeInactive?: boolean;
};

export function bookableBookingObjectsQueryKey({
  startDate,
  endDate,
  bookingType,
  includeInactive = false
}: FetchBookableBookingObjectsParams) {
  return ["bookableBookingObjects", startDate, endDate, bookingType, includeInactive] as const;
}

export async function fetchBookableBookingObjects(
  params: FetchBookableBookingObjectsParams
): Promise<BookableBookingObjectDto[]> {
  const { startDate, endDate, bookingType, includeInactive = false } = params;
  const { data } = await Axios.get<BookableBookingObjectDto[]>(
    "/booking-schedule/bookable-booking-objects",
    { params: { startDate, endDate, bookingType, includeInactive } }
  );
  return data;
}

export type UseRemoteFetchBookableBookingObjectsArgs = {
  enabled: boolean;
  /** Visible range start (same semantics as schedule calendar `firstDay`), ISO-8601. */
  startDate: string | undefined;
  /** Visible range end (same as calendar `lastDay`), ISO-8601. */
  endDate: string | undefined;
  /** Optional booking type filter for reducing resource catalog payload. */
  bookingType: BookingType;
  /** Include inactive/blocked resources in the resource calendar picker. */
  includeInactive?: boolean;
};

export function useRemoteFetchBookableBookingObjects({
  enabled,
  startDate,
  endDate,
  bookingType,
  includeInactive = false
}: Readonly<UseRemoteFetchBookableBookingObjectsArgs>) {
  const hasRange = Boolean(startDate?.length && endDate?.length);

  return useRemoteQuery<BookableBookingObjectDto[]>({
    queryKey: bookableBookingObjectsQueryKey({
      startDate: startDate ?? "",
      endDate: endDate ?? "",
      bookingType,
      includeInactive
    }),
    url: "/booking-schedule/bookable-booking-objects",
    params: hasRange
      ? { startDate, endDate, bookingType, includeInactive }
      : { bookingType, includeInactive },
    enabled: enabled && hasRange
  });
}
